'use client';

import React from 'react';
import Image from 'next/image';
import { IconMap } from '../IconMap';

export type TLiteralUnion<T extends U, U = string> = T | (Record<never, never> & U);

interface IconProps extends React.SVGProps<SVGSVGElement> {
  name: keyof typeof IconMap;
  size?: number | string;
  color?: string;
}

export function Icon({ name, size = 24, className = '', color = 'currentColor', ...rest }: IconProps) {
  const IconEntry = (IconMap as Record<string, unknown>)[name as string];
  if (!IconEntry) return null;

  const isReactComponent =
    typeof IconEntry === 'function' ||
    (typeof IconEntry === 'object' &&
      IconEntry !== null &&
      ('$$typeof' in (IconEntry as object) || 'render' in (IconEntry as object)));

  if (isReactComponent) {
    const Component = IconEntry as React.FC<React.SVGProps<SVGSVGElement>>;
    return (
      <Component
        width={size}
        height={size}
        className={className}
        color={color}
        aria-hidden="true"
        focusable="false"
        {...rest}
      />
    );
  }

  const src =
    typeof IconEntry === 'string'
      ? (IconEntry as string)
      : (IconEntry as any)?.src || (IconEntry as any)?.default || '';

  if (!src) return null;
  return (
    <Image
      src={src}
      width={typeof size === 'number' ? size : undefined}
      height={typeof size === 'number' ? size : undefined}
      className={className}
      alt={name}
      {...(rest as any)}
    />
  );
}
